/**
 * Per-handler auth wrapper for unary RPCs. Verifies the token, attaches
 * the auth context to the call, and enforces a per-method required-scope
 * table.
 *
 * Lifted from provisioning's
 * `server/grpc/interceptors/auth/server/withAuthUnary.ts` with two
 * narrow changes from the source:
 *
 * 1. The required-scope table moves from a hardcoded constant to the
 *    `requiredScopesByMethod` parameter. Provisioning's table lives in
 *    its own bootstrap.
 * 2. The local-dev short-circuit (`if (env.LOCAL) return handler(...)`)
 *    moves to the `skipAuth` parameter — pass `() => process.env.LOCAL === "true"`
 *    to preserve provisioning's behavior.
 *
 * Behavior is otherwise identical: same `Bearer` parsing, same call shape,
 * same error responses, same scope check.
 */
import {
  type ServerUnaryCall,
  type ServiceError,
  type handleUnaryCall,
  type sendUnaryData,
  status,
} from "@grpc/grpc-js";
import type {
  S2SAuthContext,
  ServerUnaryCallWithAuth,
} from "../types/auth-context";
import {
  UserActorEnvelopeError,
  X_USER_CONTEXT_HEADER,
  decodeUserActorEnvelope,
} from "../types/user-actor";
import { getGrpcAuthConfigOrNull } from "../configure";
import { userContextStore } from "../user-context-als";
import { verifyS2SToken } from "../verify/s2s-verify";
import { grpcError, grpcStatusForVerifyError } from "./grpc-error";
import { requireScope } from "./require-scope";

export type WithAuthUnaryOptions = {
  expectedAudience: string;
  expectedIssuer: string;
  /**
   * Maps RPC method names to a required scope. If a method appears in
   * this map, the wrapped handler will reject calls whose token lacks
   * the listed scope with PERMISSION_DENIED.
   */
  requiredScopesByMethod?: Record<string, string>;
  /**
   * If provided and returns true, auth is skipped entirely and the
   * handler runs as if unauthenticated. Provisioning passes
   * `() => env.LOCAL` to keep local dev frictionless.
   */
  skipAuth?: () => boolean;
  /**
   * Default-DENY for unlisted methods (request e95b3100 / D429).
   *
   * OMITTED (the common case) → inherits `configureGrpcAuth`'s
   * `denyUnlistedMethods`, which DEFAULTS TO `true` as of 0.15.0. Before
   * 0.15.0 this option defaulted to `false` INDEPENDENTLY of the global
   * config, so a service that set `denyUnlistedMethods: true` globally still
   * ran every `withAuthUnary` handler fail-open — the global switch simply did
   * not reach this code path. Two switches, one of which silently won, is
   * worse than one; there is now one.
   *
   * `false` explicitly → skip the gate for unlisted methods (fail-open,
   * deliberate). `true` explicitly → reject unlisted methods with
   * PERMISSION_DENIED naming the method.
   */
  denyUnlistedMethods?: boolean;
};

export function withAuthUnary(opts: WithAuthUnaryOptions) {
  const required = opts.requiredScopesByMethod ?? {};

  return <TReq, TRes>(
    methodName: string,
    handler: handleUnaryCall<TReq, TRes>,
  ) => {
    return async (
      call: ServerUnaryCall<TReq, TRes>,
      callback: sendUnaryData<TRes>,
    ) => {
      // The auth interceptor mutates the call object in place; the
      // augmentation type carries the `auth` field grpc-js can't see.
      const authCall = call as unknown as ServerUnaryCallWithAuth<TReq, TRes>;

      if (opts.skipAuth?.()) {
        return handler(call, callback);
      }

      const authHeader = call.metadata.get("authorization")[0];

      if (typeof authHeader !== "string" || !authHeader.startsWith("Bearer ")) {
        callback(
          grpcError(
            status.UNAUTHENTICATED,
            "Missing or invalid Authorization header",
          ),
          null,
        );
        return;
      }

      const token = authHeader.slice("Bearer ".length);

      try {
        const verified = await verifyS2SToken(token, {
          expectedAudience: opts.expectedAudience,
          expectedIssuer: opts.expectedIssuer,
        });

        authCall.auth = {
          serviceId: verified.serviceId,
          serviceName: verified.serviceName,
          scopes: verified.scopes,
          instanceId: verified.instanceId,
        } satisfies S2SAuthContext;

        // A2 / D454 — DECODE ON RECEIVE (hop-gated). Only reached inside the
        // S2S-verified block, so an `x-user-context` on an unauthenticated hop
        // is never decoded (auth already rejected it above). If present, decode
        // it onto `call.auth.userActor` so the downstream cross-tenant fence
        // (`enrichAuthContext`) has the authoritative user tenant. A malformed
        // envelope on an AUTHENTICATED hop is an attack, not a no-op → fail
        // closed with INVALID_ARGUMENT.
        const rawUserContext = call.metadata.get(X_USER_CONTEXT_HEADER)[0];
        if (typeof rawUserContext === "string" && rawUserContext.length > 0) {
          try {
            authCall.auth.userActor = decodeUserActorEnvelope(rawUserContext);
          } catch (decodeErr) {
            const detail =
              decodeErr instanceof UserActorEnvelopeError
                ? decodeErr.message
                : "x-user-context: malformed user-context envelope";
            callback(grpcError(status.INVALID_ARGUMENT, detail), null);
            return;
          }
        }

        const requiredScope = required[methodName];
        if (requiredScope) {
          try {
            requireScope(authCall.auth, requiredScope);
          } catch (err) {
            // `requireScope` throws a `ServiceError` produced by `grpcError`.
            callback(err as ServiceError, null);
            return;
          }
        } else if (
          opts.denyUnlistedMethods ??
          getGrpcAuthConfigOrNull()?.denyUnlistedMethods ??
          true
        ) {
          // F1.5 default-DENY (request e95b3100 / D429): reject unlisted
          // methods instead of skipping the gate on token-validity alone.
          callback(
            grpcError(
              status.PERMISSION_DENIED,
              `Method not bound to a required scope (denyUnlistedMethods): ${methodName}`,
            ),
            null,
          );
          return;
        }

        // A2 / D454 — SERVER-SIDE CAPTURE. When an inbound `x-user-context` is
        // present, run the handler inside the ambient store so the client auth
        // interceptor on any outgoing S2S hop re-attaches the SAME raw header
        // (user identity propagates A→B). Absent header → run normally (no
        // ambient store, so nothing is forwarded on a pure S2S-only call).
        if (typeof rawUserContext === "string" && rawUserContext.length > 0) {
          return userContextStore.run(rawUserContext, () =>
            handler(call, callback),
          );
        }
        return handler(call, callback);
      } catch (err) {
        // NOT a blanket UNAUTHENTICATED — see `grpcStatusForVerifyError`.
        // This entry point does not itself wire a replayStore, so it cannot
        // throw the outage error today; it is mapped here anyway because the
        // defect this fixes was precisely that each TS entry point decided the
        // status locally and one of them got it wrong. Go and Python map
        // centrally regardless of call site, and so does TS now.
        const message = err instanceof Error ? err.message : "Invalid token";
        callback(grpcError(grpcStatusForVerifyError(err), message), null);
      }
    };
  };
}
